/**
 * @file Comportement d'arme 'missile' : Missile : trajectoire en cloche verrouillee sur la position de la cible au tir, explosion de grand rayon a l'arrivee.
 */
import type { SimContext } from '../../Context';
import { explode, isShootable, rollCrit } from '../../combat/damage';
import type { With } from '../../ecs/World';
import { spawnProjectile } from '../projectiles';
import type { HitOutcome, ShotContext, WeaponBehaviorModule } from '../WeaponBehavior';
import { announceShot } from './common';

/**
 * fire : arc (depart -> position verrouillee) parcouru en dist / projectileSpeed secondes ; le z visuel (4h t (1-t)) est calcule par
 * le rendu. Pas de collision en vol ; explosion a l'arrivee (onExpire).
 */
export const missileBehavior: WeaponBehaviorModule = {
  id: 'missile',
  fire(ctx: SimContext, shot: ShotContext): void {
    const { weapon: w, cat } = shot;
    const p = w.params;
    const t = shot.target;
    const ex = t ? t.transform.x : shot.originX + Math.cos(shot.aimAngle) * w.range;
    const ey = t ? t.transform.y : shot.originY + Math.sin(shot.aimAngle) * w.range;
    const d = Math.hypot(ex - shot.originX, ey - shot.originY);
    const duration = Math.max(0.3, d / p.projectileSpeed);
    const roll = rollCrit(ctx, w, w.damage);
    const proj = spawnProjectile(ctx, {
      kind: 'missile',
      behavior: 'missile',
      ownerId: cat.id,
      weaponDefId: w.def.id,
      slot: w.slot,
      x: shot.originX,
      y: shot.originY,
      angle: Math.atan2(ey - shot.originY, ex - shot.originX),
      speed: 0,
      radius: p.projectileRadius,
      range: w.range + 10,
      life: duration + 1,
      damage: roll.damage,
      crit: roll.crit,
      explosionRadius: w.explosionRadius,
      arc: { sx: shot.originX, sy: shot.originY, ex, ey, t: 0, duration, height: p.missileArcHeight },
      lockedTargetId: t ? t.id : null,
    });
    announceShot(ctx, shot, 'missile', [proj.id]);
  },
  step(ctx: SimContext, proj: With<'projectile'>, dt: number): void {
    const pr = proj.projectile;
    const arc = pr.arc;
    if (!arc) {
      pr.lifeLeft = 0;
      return;
    }
    if (ctx.config.behaviors.missile.missileHoming && pr.lockedTargetId !== null) {
      const t = ctx.rt.world.get(pr.lockedTargetId);
      if (t && isShootable(ctx, t)) {
        arc.ex = t.transform.x;
        arc.ey = t.transform.y;
      }
    }
    arc.t = Math.min(1, arc.t + dt / arc.duration);
    const nx = arc.sx + (arc.ex - arc.sx) * arc.t;
    const ny = arc.sy + (arc.ey - arc.sy) * arc.t;
    proj.transform.rot = Math.atan2(ny - proj.transform.y, nx - proj.transform.x);
    proj.transform.x = nx;
    proj.transform.y = ny;
    if (arc.t >= 1) pr.lifeLeft = 0;
  },
  onHit(): HitOutcome {
    return { consumed: false };
  },
  onExpire(ctx: SimContext, proj: With<'projectile'>): void {
    const pr = proj.projectile;
    const x = pr.arc ? pr.arc.ex : proj.transform.x;
    const y = pr.arc ? pr.arc.ey : proj.transform.y;
    explode(ctx, x, y, pr.explosionRadius, pr.damage, pr.crit, 'missile', ctx.config.behaviors.missile.explosionFalloffAtEdge);
  },
};
